import useCrudFilters, { CrudFilters } from "../../../../hooks/useCrudFilters";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { relationSchema } from "../../../relation/domain/models/RelationModel";
import { customerSchema } from "../../../customer/domain/model/CustomerModel";
import { requestTypeLists } from "../../../request/domain/lists/requestTypeLists";
import { EntitySelectField } from "../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { LicencePlateSelectField } from "../../../general/fields/modules/commons/licencePlateSelectField/licencePlateSelectField";
import { ReactSelectOptionType } from "../../../../shared/types/reactSelectType";
import { TableFiltersWrapper } from "../../../general/table/modules/commons/tableFilters/tableFilters";
import { TableFilterInputWrapper } from "../../../general/table/modules/commons/tableFilters/tableFilters";
import { TableFilters } from "../../../general/table/modules/commons/tableFilters/tableFilters";

export const TireSetsTableFilters = ({
  updateFilter,
  removeFilter,
  getFilter,
  filters,
  resetFilters,
  removeFilters,
  updateFilters,
}: CrudFilters) => {
  const typeValue = getFilter("type");
  const licencesValue = getFilter("car.licence", "in");
  const vinValue = getFilter("car.vin", "contains") as string;
  const relationValue = getFilter("relation_id");
  const customerValue = getFilter("car.customer.id");
  const requestTypeValue = getFilter("storedLatestRequest.type");
  const requestStateValue = getFilter("storedLatestRequest.state");
  const freeStockValue = getFilter("car.freestock_tire");
  const inHotelValue = getFilter("in_hotel");
  const additionalInfoValue = getFilter(
    "car.additional_info",
    "contains",
  ) as string;
  const pickupAndDeliveryDateValue = getFilter(
    "storedLatestRequest.pickup_and_delivery_date",
  );
  const inOutStorageDateValue = getFilter("storedLatestRequest.in_out_storage");
  const { t } = useTranslation("common");

  const relationFilters = useCrudFilters({
    searchBy: "SearchByRootCustomer",
    fields: {},
  });

  const filteredRequestTypeLists = requestTypeLists.filter(
    (type) => type !== "cancelled" && type !== "refused",
  );

  return (
    <TableFiltersWrapper>
      <TableFilters>
        <TableFilterInputWrapper md={8}>
          <label>{t("Licence plates")}</label>
          <LicencePlateSelectField
            onChange={(options: unknown) => {
              const selectedOptions = options as ReactSelectOptionType[];

              if (options === null || selectedOptions.length === 0) {
                removeFilter("car.licence");
                return;
              }

              const selectedOptionsStringArray = selectedOptions.map(
                (option) => {
                  return option.label.replace(/-/g, "").toUpperCase();
                },
              );

              updateFilter("car.licence", selectedOptionsStringArray, "in");
            }}
            value={licencesValue}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper md={4}>
          <label>{t("Search by car")}</label>
          <Form.Control
            type="text"
            placeholder={t("Search by car")}
            value={vinValue ?? additionalInfoValue ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilters([
                  {
                    key: "car.vin",
                  },
                  {
                    key: "car.additional_info",
                  },
                  {
                    key: "car.licence",
                  },
                ]);
              } else {
                updateFilters([
                  {
                    key: "car.vin",
                    value,
                    type: "contains",
                    condition: "or",
                  },
                  {
                    key: "car.additional_info",
                    value,
                    type: "contains",
                    condition: "or",
                  },
                  {
                    key: "car.licence",
                    value: value.replace(/-/g, "").toUpperCase(),
                    type: "contains",
                    condition: "or",
                  },
                ]);
              }
            }}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper md={3}>
          <label>{t("Dealer")}</label>
          <EntitySelectField
            entity={"Relation"}
            prefix={"relations"}
            onChange={(option) => {
              if (option == null) {
                removeFilter("relation_id");
                removeFilter("car.customer.id");
                relationFilters.removeFilter("relation.id");
                return;
              }

              const { value } = option as { value: string };
              removeFilter("car.customer.id");
              updateFilter("relation_id", value);
              relationFilters.updateFilter("relation.id", value);
            }}
            limit={100}
            clearable={true}
            schema={relationSchema}
            value={relationValue}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper md={2}>
          <label>{t("Customer name")}</label>
          <EntitySelectField
            entity={"Customer"}
            prefix={"customers"}
            onChange={(option) => {
              if (option == null) {
                removeFilter("car.customer.id");
                return;
              }
              const { value } = option as { value: string };
              updateFilter("car.customer.id", value);
            }}
            limit={100}
            clearable={true}
            schema={customerSchema}
            filters={relationFilters.filters}
            value={customerValue}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("Pickup and delivery date")}</label>
          <Form.Control
            type={"date"}
            value={`${pickupAndDeliveryDateValue ?? ""}`}
            onChange={(event) => {
              updateFilter(
                "storedLatestRequest.pickup_and_delivery_date",
                event.target.value,
              );
            }}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("In/out storage")}</label>
          <Form.Control
            type={"date"}
            value={`${inOutStorageDateValue ?? ""}`}
            onChange={(event) => {
              updateFilter(
                "storedLatestRequest.in_out_storage",
                event.target.value,
              );
            }}
          />
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("Request")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("storedLatestRequest.type");
              } else {
                updateFilter("storedLatestRequest.type", event.target.value);
              }
            }}
            value={`${requestTypeValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"deliver"}>{t("Deliver")}</option>
            <option value={"pickup"}>{t("Pickup")}</option>
          </Form.Select>
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("State")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("storedLatestRequest.state");
              } else {
                updateFilter("storedLatestRequest.state", event.target.value);
              }
            }}
            value={`${requestStateValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            {filteredRequestTypeLists.map((type, i) => {
              return (
                <option key={i} value={type}>
                  {t(type)}
                </option>
              );
            })}
          </Form.Select>
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("In hotel")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("in_hotel");
              } else {
                updateFilter("in_hotel", event.target.value === "1");
              }
            }}
            value={`${inHotelValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"1"}>{t("Yes")}</option>
            <option value={"0"}>{t("No")}</option>
          </Form.Select>
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("Type band")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("type");
              } else {
                updateFilter("type", event.target.value);
              }
            }}
            value={`${typeValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"summer"}>{t("Summer")}</option>
            <option value={"winter"}>{t("Winter")}</option>
            <option value={"all_season"}>{t("All season")}</option>
          </Form.Select>
        </TableFilterInputWrapper>

        <TableFilterInputWrapper>
          <label>{t("Free stock")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("car.freestock_tire");
              } else {
                updateFilter("car.freestock_tire", event.target.value === "1");
              }
            }}
            value={`${freeStockValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"1"}>{t("Yes")}</option>
            <option value={"0"}>{t("No")}</option>
          </Form.Select>
        </TableFilterInputWrapper>
      </TableFilters>
      {filters?.fields && Object.keys(filters.fields).length > 0 && (
        <Button
          onClick={resetFilters}
          size={"sm"}
          className="reset-filters"
          variant="light"
        >
          {t("Reset filters")}
        </Button>
      )}
    </TableFiltersWrapper>
  );
};
